import * as logger from "../common/logger";
import FileUpload from "./FileUpload";
import { Button } from "@mui/material";
import { createFileEntry } from "fileUtils";
import { useSnackbar } from "notistack";
import { useCallback, useImperativeHandle, useState } from "react";
import * as uuid from "uuid";

const FilesFileUpload = ({
  onDrop,
  onDropWithPath,
  onCreateEmptyDirectory,
  oneAtATime = true,
  disabled = false,
  size,
  asAtDateNotRequired,
  dropMessage = "Drop an .xlsx, .csv, or .tsv file here to upload",
  updateAsAtDate,
  asAtDate,
  recommendedAsAtDate,
  uploadInitiated = null,
  waitText = false,
  mimeTypes = null,
  aborter = null,
  options = null,
  title = null,
  showLoadingOverlay = false,
  loadingMessage = null,
}) => {
  const [runningFiles, setRunningFiles] = useState({});

  const addFile = (fileControl) => {
    setRunningFiles((rf) => ({
      ...rf,
      [fileControl.entry]: fileControl,
    }));
  };
  const removeFile = (fileControl) => {
    setRunningFiles((rf) => {
      const { [fileControl.entry]: _, ...rest } = rf;
      return rest;
    });
  };

  useImperativeHandle(
    aborter,
    () => ({
      abort: () => {
        setLoadingFiles(false);
        for (const index in runningFiles) {
          const fileControl = runningFiles[index];
          fileControl.abort = true;
        }
      },
    }),
    [runningFiles]
  );

  const {
    enqueueSnackbar: queueMessage,
    closeSnackbar: closeMessage,
  } = useSnackbar();
  const [loadingFiles, setLoadingFiles] = useState(false);

  const processFile = useCallback(
    (file, relativePath = null, onComplete = null) => {
      const fileControl = {
        entry: uuid.v4(),
        file,
        abort: false,
      };
      if (typeof uploadInitiated === "function") uploadInitiated(file);

      createFileEntry(file, waitText, mimeTypes)
        .then((id) => {
          removeFile(fileControl);
          if (fileControl.abort) return;
          if (onDropWithPath && relativePath) {
            onDropWithPath(id, file, relativePath);
          } else {
            onDrop(id, file);
          }
        })
        .catch((error) => {
          removeFile(fileControl);
          if (fileControl.abort) return;
          logger.error("ERROR", error);
          const reason = error?.message || `${error}`;
          queueMessage(`File "${file.name}" cannot be uploaded [${reason}].`, {
            variant: "error",
            autoHideDuration: 10_000,
            action: (msgId) => (
              <Button
                color={"secondary"}
                size={"small"}
                onClick={() => closeMessage(msgId)}
              >
                {"Dismiss"}
              </Button>
            ),
          });
          logger.error("Error uploading file", { breadcrumb: { file } });
        })
        .finally(() => {
          if (typeof onComplete === "function") onComplete();
        });
      return fileControl;
    },
    [
      onDrop,
      onDropWithPath,
      uploadInitiated,
      waitText,
      mimeTypes,
      queueMessage,
      closeMessage,
    ]
  );

  const dropCatch = useCallback(
    (files) => {
      if (files.length === 0) {
        return;
      }
      setLoadingFiles(true);
      const filesToProcess = oneAtATime ? [files[0]] : files;
      let filecount = filesToProcess.length;
      const dec = () => {
        filecount -= 1;
        if (filecount === 0) {
          setLoadingFiles(false);
        }
      };
      filesToProcess.forEach((file) => {
        const control = processFile(file, null, dec);
        addFile(control);
      });
    },
    [processFile, oneAtATime]
  );

  const dropCatchWithPaths = useCallback(
    (filesWithPaths) => {
      if (!filesWithPaths || filesWithPaths.length === 0) {
        return;
      }

      const filesToUpload = filesWithPaths.filter(
        (item) => !item.isEmptyDirectory
      );
      const emptyDirectories = filesWithPaths.filter(
        (item) => item.isEmptyDirectory
      );

      emptyDirectories.forEach(({ relativePath }) => {
        if (onCreateEmptyDirectory) {
          onCreateEmptyDirectory(relativePath);
        }
      });

      if (filesToUpload.length === 0) {
        return;
      }

      setLoadingFiles(true);
      let filecount = filesToUpload.length;
      const dec = () => {
        filecount -= 1;
        if (filecount === 0) {
          setLoadingFiles(false);
        }
      };
      filesToUpload.forEach(({ file, relativePath }) => {
        const control = processFile(file, relativePath, dec);
        addFile(control);
      });
    },
    [processFile, onCreateEmptyDirectory]
  );
  return (
    <FileUpload
      {...{
        onDrop: dropCatch,
        onDropWithPaths: onDropWithPath ? dropCatchWithPaths : undefined,
        disabled,
        loading: loadingFiles,
        size,
        asAtDateNotRequired,
        dropMessage,
        updateAsAtDate,
        asAtDate,
        recommendedAsAtDate,
        options,
        title,
        showLoadingOverlay,
        loadingMessage,
      }}
    />
  );
};

export default FilesFileUpload;
